import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useMatch } from 'react-router';
import chatHistoryService, { type ChatHistoryItem } from '../../utils/chatHistoryService';
import { MdKeyboardArrowDown, MdLogout } from 'react-icons/md';
import Cookies from 'js-cookie';
import apiRequest from '../../utils/request';
import SidebarButton from './SidebarButton';
import { SidebarExplorer } from '@podus/ui';
import { PRODUCT_URLS } from '@podus/utils';
import { useAppShell } from '../container/AppShell';

import { MdOutlineCreateNewFolder } from 'react-icons/md';
import { MdOutlineFolder } from 'react-icons/md';
import { MdOutlineAccountBalance } from 'react-icons/md';
import { MdOutlineSettings } from 'react-icons/md';
import { MdOutlineHeadphones } from 'react-icons/md';

interface UserProfile {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    profilePic?: string;
}

export interface ChatSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    onOpenHelpModal?: () => void;
    onOpenCreateCaseModal?: () => void;
    onOpenSettingsModal?: () => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
    isOpen,
    onToggle,
    isCollapsed = false,
    onToggleCollapse,
    onOpenHelpModal,
    onOpenCreateCaseModal,
    onOpenSettingsModal,
}) => {
    const navigate = useNavigate();
    const chatMatch = useMatch('/c/:chatId');
    const currentChatId = chatMatch?.params?.chatId;
    const { sidebarRefreshTrigger: refreshTrigger } = useAppShell();
    const [chatHistories, setChatHistories] = useState<ChatHistoryItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<UserProfile | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch user profile
    useEffect(() => {
        const fetchUserProfile = async () => {
            const token = Cookies.get('p-ai-accessToken');
            if (!token) {
                setUser(null);
                return;
            }

            try {
                const data = await apiRequest.request<{
                    statusCode: number;
                    response?: { data?: UserProfile };
                }>({
                    method: 'get',
                    url: '/users/profile',
                });

                if (data?.statusCode === 200 && data?.response?.data) {
                    setUser(data.response.data);
                } else {
                    setUser(null);
                }
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
                setUser(null);
            }
        };

        fetchUserProfile();
    }, [refreshTrigger]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    // Fetch chat histories
    useEffect(() => {
        const fetchChatHistories = async () => {
            setIsLoading(true);
            try {
                const response = await chatHistoryService.getChatHistories(1, 40);
                if (response.statusCode === 200 && response.response?.data) {
                    setChatHistories(response.response.data);
                }
            } catch (error) {
                console.error('Failed to fetch chat histories:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchChatHistories();
    }, [refreshTrigger]);

    const handleNewChat = () => {
        navigate('/');
        if (window.innerWidth < 1024) {
            onToggle(); // Close sidebar on mobile
        }
    };

    const handleSelectChat = (chatId: string) => {
        navigate(`/c/${chatId}`);
        if (window.innerWidth < 1024) {
            onToggle(); // Close sidebar on mobile after selection
        }
    };

    const handleLogout = async () => {
        setIsDropdownOpen(false);
        // Tell NEW_PODUS_API to bump tokensValidAfter so every JWT ever issued
        // to this user is rejected going forward. Swallow errors so failure of
        // this call never blocks local sign-out.
        try {
            await apiRequest.request({ method: 'post', url: 'acquisition/logout' });
        } catch (e) {
            console.warn('logout API call failed', e);
        }
        Cookies.remove('p-ai-accessToken');
        Cookies.remove('p-ai-uid');
        setUser(null);
        // Bounce through auth's /sign-out so it clears its own cookie too —
        // otherwise /sign-in auto-redirects back with the same token and the
        // user loops.
        const AUTH_BASE_URL =
            window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://auth.podus.org';
        const redirectTarget = `${window.location.origin}/`;
        window.location.href = `${AUTH_BASE_URL}/sign-out?redirect=${encodeURIComponent(redirectTarget)}`;
    };

    const handleHelpSupport = () => {
        if (onOpenHelpModal) {
            onOpenHelpModal();
        }
        if (window.innerWidth < 1024) {
            onToggle(); // Close sidebar on mobile
        } else if (onToggleCollapse && !isCollapsed) {
            onToggleCollapse(); // Collapse sidebar on desktop
        }
    };

    const handleCreateCase = () => {
        if (onOpenCreateCaseModal) {
            onOpenCreateCaseModal();
        }
        if (window.innerWidth < 1024) {
            onToggle(); // Close sidebar on mobile
        } else if (onToggleCollapse && !isCollapsed) {
            onToggleCollapse(); // Collapse sidebar on desktop
        }
    };

    const handleCreateMultidoorCase = () => {
        const base = PRODUCT_URLS.podusmultidoor.replace(/\/$/, '');
        window.open(`${base}/dashboard/create-case`, '_blank', 'noopener,noreferrer');
        if (window.innerWidth < 1024) {
            onToggle(); // Close sidebar on mobile
        } else if (onToggleCollapse && !isCollapsed) {
            onToggleCollapse(); // Collapse sidebar on desktop
        }
    };

    const handleSettings = () => {
        if (onOpenSettingsModal) {
            onOpenSettingsModal();
        }
        if (window.innerWidth < 1024) {
            onToggle(); // Close sidebar on mobile
        } else if (onToggleCollapse && !isCollapsed) {
            onToggleCollapse(); // Collapse sidebar on desktop
        }
    };

    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40 backdrop-blur-[2px] lg:hidden"
                    onClick={onToggle}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed top-0 left-0 bg-background backdrop-blur-md z-50
                    transform transition-all duration-700 ease-in-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    ${isCollapsed ? 'w-16 lg:w-16' : 'w-80 lg:w-80'}
                    lg:translate-x-0 lg:static lg:z-auto
                    flex flex-col
                    border-r border-[#e1e1e1]
                    ios-safe-area-top
                `}
                style={{ height: '100dvh' }}
            >
                <div className="bg-white/60 h-full flex flex-col flex-1">
                    {/* Logo/Brand */}
                    <div
                        className={`h-[60px] items-center flex relative ${isCollapsed ? 'lg:justify-center lg:items-center lg:px-0' : 'px-4 gap-2 justify-between'}`}
                    >
                        <div>
                            <img
                                src="/assets/logo.svg"
                                alt="Podus AI"
                                className={`ml-[10px] h-auto w-[100px] transition-opacity duration-50 ease-in-out ${!isCollapsed ? 'opacity-100' : 'opacity-0 absolute'}`}
                            />
                        </div>
                        <button
                            onClick={onToggleCollapse}
                            className="cursor-pointer hidden lg:flex w-10 h-10 rounded-full items-center justify-center transition-all duration-200 hover:bg-[#e1e1e1]"
                            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            <span
                                className={`transition-transform duration-500 ${isCollapsed ? 'rotate-180' : 'rotate-0'}`}
                            >
                                {/* <IoIosArrowRoundBack size={26} /> */}
                                <svg
                                    width={isCollapsed ? '24' : '20'}
                                    height={isCollapsed ? '24' : '20'}
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                    data-rtl-flip=""
                                    className="icon max-md:hidden transition-all duration-300"
                                >
                                    <path d="M6.83496 3.99992C6.38353 4.00411 6.01421 4.0122 5.69824 4.03801C5.31232 4.06954 5.03904 4.12266 4.82227 4.20012L4.62207 4.28606C4.18264 4.50996 3.81498 4.85035 3.55859 5.26848L3.45605 5.45207C3.33013 5.69922 3.25006 6.01354 3.20801 6.52824C3.16533 7.05065 3.16504 7.71885 3.16504 8.66301V11.3271C3.16504 12.2712 3.16533 12.9394 3.20801 13.4618C3.25006 13.9766 3.33013 14.2909 3.45605 14.538L3.55859 14.7216C3.81498 15.1397 4.18266 15.4801 4.62207 15.704L4.82227 15.79C5.03904 15.8674 5.31234 15.9205 5.69824 15.9521C6.01398 15.9779 6.383 15.986 6.83398 15.9902L6.83496 3.99992ZM18.165 11.3271C18.165 12.2493 18.1653 12.9811 18.1172 13.5702C18.0745 14.0924 17.9916 14.5472 17.8125 14.9648L17.7295 15.1415C17.394 15.8 16.8834 16.3511 16.2568 16.7353L15.9814 16.8896C15.5157 17.1268 15.0069 17.2285 14.4102 17.2773C13.821 17.3254 13.0893 17.3251 12.167 17.3251H7.83301C6.91071 17.3251 6.17898 17.3254 5.58984 17.2773C5.06757 17.2346 4.61294 17.1508 4.19531 16.9716L4.01855 16.8896C3.36014 16.5541 2.80898 16.0434 2.4248 15.4169L2.27051 15.1415C2.03328 14.6758 1.93158 14.167 1.88281 13.5702C1.83468 12.9811 1.83496 12.2493 1.83496 11.3271V8.66301C1.83496 7.74072 1.83468 7.00898 1.88281 6.41985C1.93157 5.82309 2.03329 5.31432 2.27051 4.84856L2.4248 4.57317C2.80898 3.94666 3.36012 3.436 4.01855 3.10051L4.19531 3.0175C4.61285 2.83843 5.06771 2.75548 5.58984 2.71281C6.17898 2.66468 6.91071 2.66496 7.83301 2.66496H12.167C13.0893 2.66496 13.821 2.66468 14.4102 2.71281C15.0069 2.76157 15.5157 2.86329 15.9814 3.10051L16.2568 3.25481C16.8833 3.63898 17.394 4.19012 17.7295 4.84856L17.8125 5.02531C17.9916 5.44285 18.0745 5.89771 18.1172 6.41985C18.1653 7.00898 18.165 7.74072 18.165 8.66301V11.3271ZM8.16406 15.995H12.167C13.1112 15.995 13.7794 15.9947 14.3018 15.9521C14.8164 15.91 15.1308 15.8299 15.3779 15.704L15.5615 15.6015C15.9797 15.3451 16.32 14.9774 16.5439 14.538L16.6299 14.3378C16.7074 14.121 16.7605 13.8478 16.792 13.4618C16.8347 12.9394 16.835 12.2712 16.835 11.3271V8.66301C16.835 7.71885 16.8347 7.05065 16.792 6.52824C16.7605 6.14232 16.7073 5.86904 16.6299 5.65227L16.5439 5.45207C16.32 5.01264 15.9796 4.64498 15.5615 4.3886L15.3779 4.28606C15.1308 4.16013 14.8165 4.08006 14.3018 4.03801C13.7794 3.99533 13.1112 3.99504 12.167 3.99504H8.16406C8.16407 3.99667 8.16504 3.99829 8.16504 3.99992L8.16406 15.995Z"></path>
                                </svg>
                            </span>
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div
                        className={`border-b border-[#e1e1e1] py-4 space-y-1 flex flex-col items-center ${isCollapsed ? 'lg:px-0' : 'px-4'}`}
                    >
                        <SidebarButton
                            icon={<MdOutlineCreateNewFolder className="w-5 h-5 shrink-0" />}
                            label="New Chat"
                            isCollapsed={isCollapsed}
                            onClick={handleNewChat}
                        />
                        <SidebarButton
                            icon={<MdOutlineFolder className="w-5 h-5 shrink-0" />}
                            label="Create Case"
                            isCollapsed={isCollapsed}
                            onClick={handleCreateCase}
                        />
                        <SidebarButton
                            icon={<MdOutlineAccountBalance className="w-5 h-5 shrink-0" />}
                            label="Create Multi-door Case"
                            isCollapsed={isCollapsed}
                            onClick={handleCreateMultidoorCase}
                        />
                        <SidebarButton
                            icon={<MdOutlineSettings className="w-5 h-5 shrink-0" />}
                            label="Settings"
                            isCollapsed={isCollapsed}
                            onClick={handleSettings}
                        />
                        <SidebarButton
                            icon={<MdOutlineHeadphones className="w-5 h-5 shrink-0" />}
                            label="Help & Support"
                            isCollapsed={isCollapsed}
                            onClick={handleHelpSupport}
                        />
                    </div>

                    {/* History Section */}
                    <div
                        className={`flex-1 pt-[20px] overflow-y-auto overflow-x-hidden sidebar-scrollbar ${isCollapsed ? 'lg:px-0' : 'px-4'}`}
                    >
                        {/* Expanded State - Show full history */}
                        <div
                            className={!isCollapsed ? 'opacity-100 max-h-screen' : 'opacity-0 max-h-0 overflow-hidden'}
                        >
                            <h2 className="text-[12px] font-medium text-neutral-500 uppercase tracking-wider mb-3 px-2 truncate overflow-hidden">
                                History
                            </h2>
                            <div className="space-y-1">
                                {isLoading ? (
                                    <div className="space-y-1">
                                        <div className="p-2 rounded-lg">
                                            <div className="h-4 w-[85%] rounded-md skeleton-shimmer" />
                                        </div>
                                        <div className="p-2 rounded-lg">
                                            <div className="h-4 w-[70%] rounded-md skeleton-shimmer skeleton-shimmer-delay-1" />
                                        </div>
                                        <div className="p-2 rounded-lg">
                                            <div className="h-4 w-[90%] rounded-md skeleton-shimmer skeleton-shimmer-delay-2" />
                                        </div>
                                        <div className="p-2 rounded-lg">
                                            <div className="h-4 w-[65%] rounded-md skeleton-shimmer skeleton-shimmer-delay-3" />
                                        </div>
                                        <div className="p-2 rounded-lg">
                                            <div className="h-4 w-[80%] rounded-md skeleton-shimmer skeleton-shimmer-delay-4" />
                                        </div>
                                        <div className="p-2 rounded-lg">
                                            <div className="h-4 w-[75%] rounded-md skeleton-shimmer skeleton-shimmer-delay-5" />
                                        </div>
                                    </div>
                                ) : chatHistories.length === 0 ? (
                                    <div className="px-3 py-8 text-center text-secondary text-sm">
                                        No chat history yet
                                    </div>
                                ) : (
                                    chatHistories.map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => handleSelectChat(item.chatId)}
                                            className={`m-0 cursor-pointer w-full text-left p-2 rounded-lg hover:bg-[#e1e1e1] group overflow-hidden ${
                                                currentChatId === item.chatId ? 'bg-[#e1e1e1]' : ''
                                            }`}
                                        >
                                            <div className="flex items-start gap-2 overflow-hidden">
                                                <span className="text-sm text-secondary truncate overflow-hidden">
                                                    {item.chatTitle || 'New Chat'}
                                                </span>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Explore Other Products */}
                    {!isCollapsed && (
                        <SidebarExplorer
                            currentApp="podus-ai"
                            className="border-t border-background-hover px-4 py-4"
                        />
                    )}

                    {/* Bottom Menu */}
                    <div className={`space-y-1`}>
                        {user && (
                            <div ref={dropdownRef} className="relative border-t border-[#e1e1e1]">
                                <button
                                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                                    className={`cursor-pointer hover:bg-[#e1e1e1] w-full flex items-center gap-2 py-2.5  transition-all duration-300 ease-in-out overflow-hidden ${
                                        isCollapsed ? 'lg:justify-center lg:px-2' : 'px-3 justify-between'
                                    }`}
                                >
                                    <div className="flex items-center">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#151515] text-xs font-semibold text-white shrink-0">
                                            {getInitials(user.firstName, user.lastName)}
                                        </div>
                                        <span
                                            className={`ml-2 text-sm font-medium whitespace-nowrap transition-all duration-300 ease-in-out ${!isCollapsed ? 'opacity-100 w-auto ml-0' : 'opacity-0 w-0 -ml-2'}`}
                                        >
                                            {user.firstName} {user.lastName}
                                        </span>
                                    </div>
                                    <MdKeyboardArrowDown
                                        size={20}
                                        className={`shrink-0 transition-all duration-300 ease-in-out ${!isCollapsed ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}
                                    />
                                </button>

                                {isDropdownOpen && (
                                    <div
                                        className={`absolute bottom-full mb-2 bg-white shadow-lg rounded-lg border border-gray-200 ${isCollapsed ? 'left-16' : 'left-0 right-0 mx-2'}`}
                                    >
                                        <button
                                            onClick={handleLogout}
                                            className={`flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 rounded-lg ${isCollapsed ? 'whitespace-nowrap' : ''}`}
                                        >
                                            <MdLogout size={18} />
                                            <span className={isCollapsed ? '' : ''}>Log Out</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </aside>

        </>
    );
};

export default ChatSidebar;
